import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PageBanner } from '@/components/PageBanner';
import { KpiCard } from '@/components/KpiCard';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/PageSkeleton';
import { PageHeader } from '@/components/PageHeader';
import { DataTableToolbar } from '@/components/DataTableControls';
import { ViewModeToggle } from '@/components/ViewModeToggle';
import { EmptyState } from '@/components/EmptyState';
import { LimitMeter } from '@/components/LimitMeter';
import { MarkdownContent } from '@/components/MarkdownContent';
import { 
  AlertCircle, FileQuestion, MapPin,
  CheckCircle, XCircle, Eye, Tag, ChevronRight, Loader2,
  Building2, Zap,
  AlertTriangle, ThumbsDown, HelpCircle,
  LayoutGrid, List, Columns, RefreshCw, Clock, ArrowRight,
  Save, Trash2
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Gap type configuration with improved styling
const GAP_TYPE_CONFIG = {
  no_results: { 
    label: 'No Results', 
    shortLabel: 'No Results',
    color: 'bg-primary/10 text-primary border-primary/20',
    accent: 'bg-primary/60',
    iconBg: 'bg-primary/10 text-primary',
    lightBg: 'bg-card',
    icon: XCircle,
    priority: 'high'
  },
  low_confidence: { 
    label: 'Low Confidence', 
    shortLabel: 'Low Conf.',
    color: 'bg-info/10 text-info border-info/20',
    accent: 'bg-info',
    iconBg: 'bg-info/10 text-info',
    lightBg: 'bg-card',
    icon: AlertTriangle,
    priority: 'medium'
  },
  explicit_no_info: { 
    label: 'Eva Said No Info', 
    shortLabel: 'No Info',
    color: 'bg-warning/10 text-warning border-warning/20',
    accent: 'bg-warning',
    iconBg: 'bg-warning/10 text-warning',
    lightBg: 'bg-card',
    icon: HelpCircle,
    priority: 'high'
  },
  no_information: { 
    label: 'Eva Said No Info', 
    shortLabel: 'No Info',
    color: 'bg-warning/10 text-warning border-warning/20',
    accent: 'bg-warning',
    iconBg: 'bg-warning/10 text-warning',
    lightBg: 'bg-card',
    icon: HelpCircle,
    priority: 'high'
  },
  user_feedback_negative: { 
    label: 'Negative Feedback', 
    shortLabel: 'Thumbs Down',
    color: 'bg-primary/10 text-primary border-primary/20',
    accent: 'bg-primary',
    iconBg: 'bg-primary/10 text-primary',
    lightBg: 'bg-card',
    icon: ThumbsDown,
    priority: 'medium'
  },
  // Gap types emitted by the active chat pipeline (chat_pipeline.py) and the
  // legacy report_content_gap tool (chat_tools.py). Kept in sync so the detail
  // pill renders a styled label instead of a raw value.
  missing_document: {
    label: 'Missing Doc',
    shortLabel: 'Missing Doc',
    color: 'bg-warning/10 text-warning border-warning/20',
    accent: 'bg-warning',
    iconBg: 'bg-warning/10 text-warning',
    lightBg: 'bg-card',
    icon: FileQuestion,
    priority: 'high'
  },
  insufficient_detail: {
    label: 'Insufficient Detail',
    shortLabel: 'Low Detail',
    color: 'bg-info/10 text-info border-info/20',
    accent: 'bg-info',
    iconBg: 'bg-info/10 text-info',
    lightBg: 'bg-card',
    icon: AlertTriangle,
    priority: 'medium'
  },
  outdated: {
    label: 'Outdated',
    shortLabel: 'Outdated',
    color: 'bg-primary/10 text-primary border-primary/20',
    accent: 'bg-primary',
    iconBg: 'bg-primary/10 text-primary',
    lightBg: 'bg-card',
    icon: Clock,
    priority: 'medium'
  },
  off_topic: {
    label: 'Off Topic',
    shortLabel: 'Off Topic',
    color: 'bg-muted text-muted-foreground border-border',
    accent: 'bg-gray-300',
    iconBg: 'bg-muted text-muted-foreground',
    lightBg: 'bg-card',
    icon: HelpCircle,
    priority: 'low'
  },
};

// Humanize any gap_type the backend emits that isn't explicitly mapped above
// (future taxonomy additions) so the pill never shows a raw snake_case value.
const humanizeGapType = (value) =>
  (value || 'Unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const resolveGapTypeConfig = (gapType) =>
  GAP_TYPE_CONFIG[gapType] || {
    ...GAP_TYPE_CONFIG.no_results,
    label: humanizeGapType(gapType),
    shortLabel: humanizeGapType(gapType),
  };

// Status configuration
const STATUS_CONFIG = {
  new: { 
    label: 'New', 
    color: 'bg-info/10 text-info border-info/20',
    dot: 'bg-blue-500',
    accent: 'bg-blue-500',
    icon: AlertCircle,
    kpiColor: 'info'
  },
  reviewed: { 
    label: 'Reviewed', 
    color: 'bg-warning/10 text-warning border-warning/20',
    dot: 'bg-amber-500',
    accent: 'bg-amber-500',
    icon: Eye,
    kpiColor: 'warning'
  },
  addressed: { 
    label: 'Addressed', 
    color: 'bg-success/10 text-success border-success/20',
    dot: 'bg-emerald-500',
    accent: 'bg-emerald-500',
    icon: CheckCircle,
    kpiColor: 'success'
  },
  dismissed: { 
    label: 'Dismissed', 
    color: 'bg-muted text-muted-foreground border-border',
    dot: 'bg-gray-300',
    accent: 'bg-gray-300',
    icon: XCircle,
    kpiColor: 'gray'
  },
  pending: { 
    label: 'Pending', 
    color: 'bg-warning/10 text-warning border-warning/20',
    dot: 'bg-yellow-500',
    accent: 'bg-yellow-500',
    icon: AlertTriangle,
    kpiColor: 'warning'
  },
};

const TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'no_results', label: 'No Results' },
  { value: 'low_confidence', label: 'Low Confidence' },
  { value: 'explicit_no_info', label: 'No Info' },
  { value: 'user_feedback_negative', label: 'Negative Feedback' },
];

// Redesigned Gap Card
function GapCard({ gap, isSelected, onSelect, onView, viewMode, draggable = false, onDragStart }) {
  const gapTypeConfig = resolveGapTypeConfig(gap.gap_type);
  const statusConfig = STATUS_CONFIG[gap.status] || STATUS_CONFIG.new;
  const Icon = gapTypeConfig.icon;
  const isNew = gap.status === 'new';
  
  const priorityIndicator = {
    high: { label: 'High priority', icon: AlertCircle, className: 'bg-warning/10 text-warning border-warning/20' },
    medium: { label: 'Medium priority', icon: AlertTriangle, className: 'bg-muted text-muted-foreground border-border' },
    low: { label: 'Low priority', icon: CheckCircle, className: 'bg-success/10 text-success border-success/20' },
  };
  
  const priority = priorityIndicator[gapTypeConfig.priority] || priorityIndicator.medium;
  const PriorityIcon = priority.icon;
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (viewMode === 'list') {
    return (
      <div 
        className={`flex items-center gap-4 p-4 border-b border-border hover:bg-muted/50 transition-colors cursor-pointer ${
          isSelected ? 'bg-primary/5' : ''
        } ${isNew ? 'bg-blue-50/50 dark:bg-blue-950/10' : ''}`}
        onClick={() => onView(gap)}
      >
        <Checkbox 
          checked={isSelected}
          onCheckedChange={() => onSelect(gap.id)}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
        
        <div className={`p-2 rounded-lg ${gapTypeConfig.iconBg} flex-shrink-0`}>
          <Icon className="w-4 h-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground truncate">{gap.query}</p>
          <p className="text-sm text-muted-foreground truncate">{gap.response?.slice(0, 100)}...</p>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge
            variant="outline"
            className={`px-2 ${priority.className}`}
            title={priority.label}
            aria-label={priority.label}
          >
            <PriorityIcon className="h-3.5 w-3.5" />
            <span className="sr-only">{priority.label}</span>
          </Badge>
          <Badge variant="outline" className={statusConfig.color}>{statusConfig.label}</Badge>
          <span className="text-xs text-muted-foreground">{formatDate(gap.created_at)}</span>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className={`group relative flex min-h-[260px] flex-col overflow-hidden rounded-xl border border-slate-300 bg-white shadow-md shadow-slate-200/70 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg hover:shadow-slate-300/60 dark:border-border dark:bg-card dark:shadow-sm dark:shadow-none dark:hover:border-primary/25 dark:hover:shadow-md ${
        isSelected ? 'ring-2 ring-primary ring-offset-2 dark:ring-offset-background' : ''
      }`}
      onClick={() => onSelect(gap.id)}
      draggable={draggable}
      onDragStart={(event) => onDragStart?.(event, gap)}
    >
      <div className={`absolute inset-x-0 top-0 h-1.5 ${statusConfig.accent}`} aria-hidden="true" />

      <div className="flex flex-1 flex-col p-5 pl-6">
        {/* Header with badges */}
        <div className="mb-4 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className={`px-2 ${gapTypeConfig.color}`}
                title={gapTypeConfig.label}
                aria-label={gapTypeConfig.label}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="sr-only">{gapTypeConfig.label}</span>
              </Badge>
              <Badge variant="outline" className={priority.className}>
                <PriorityIcon className="h-3.5 w-3.5" />
                <span className="sr-only">{priority.label}</span>
              </Badge>
            </div>
            
            {gap.suggested_topic_name && (
              <Badge 
                variant="outline" 
                className="mt-2 max-w-full text-xs"
                style={{ borderColor: gap.topic_color || '#6366f1', color: gap.topic_color || '#6366f1' }}
              >
                <Tag className="mr-1 h-3 w-3 shrink-0" />
                <span className="truncate">{gap.suggested_topic_name}</span>
              </Badge>
            )}
          </div>
        </div>
        
        {/* Question */}
        <div className="mb-4">
          <p className="text-base font-semibold text-foreground leading-snug line-clamp-2">
            "{gap.query}"
          </p>
        </div>
        
        {/* Response preview */}
        {gap.response && (
          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-border dark:bg-muted/30">
            <p className="mb-1 text-xs font-medium text-muted-foreground">Eva response</p>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {gap.response}
            </p>
          </div>
        )}
        
        {/* Meta info */}
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDate(gap.created_at)}
          </span>
          {gap.user_country && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {gap.user_country}
            </span>
          )}
          {gap.user_region && (
              <span className="flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 dark:bg-muted">
              {gap.user_region}
            </span>
          )}
          {gap.user_department && (
            <span className="flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              {gap.user_department}
            </span>
          )}
        </div>
      </div>
      
      {/* Selection checkbox - bottom right */}
      <div className="absolute right-4 top-4">
        <Checkbox 
          checked={isSelected}
          onCheckedChange={() => onSelect(gap.id)}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="border-primary bg-white shadow-sm dark:bg-card"
        />
      </div>
      
      {/* View details button */}
      <button
        onClick={(e) => { e.stopPropagation(); onView(gap); }}
        className="flex w-full items-center justify-center gap-2 border-t border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:border-border dark:bg-muted/20 dark:text-muted-foreground dark:hover:bg-muted/40 dark:hover:text-foreground"
      >
        <Eye className="w-4 h-4" />
        View Details
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// Kanban Column
function KanbanColumn({ title, status, gaps, selectedGaps, onSelect, onView, onMoveGap }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  const StatusIcon = statusConfig.icon;
  
  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const gapId = event.dataTransfer.getData('text/plain');
    if (gapId) onMoveGap(gapId, status);
  };

  return (
    <div
      className={`flex-1 min-w-[300px] max-w-[400px] rounded-2xl border p-3 transition-all duration-150 ${
        isDragOver
          ? 'border-primary/60 bg-primary/10 shadow-lg shadow-primary/10 ring-2 ring-primary/25'
          : 'border-transparent'
      }`}
      onDragEnter={() => setIsDragOver(true)}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setIsDragOver(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsDragOver(false);
        }
      }}
      onDrop={handleDrop}
    >
      <div className="mb-4 flex items-center gap-3">
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg border ${statusConfig.color}`}>
          <StatusIcon className="h-4 w-4" />
        </div>
        <h3 className="font-semibold text-foreground">{title}</h3>
        <Badge variant="secondary" className="ml-auto">{gaps.length}</Badge>
      </div>
      
      <div className="space-y-3">
        {gaps.map(gap => (
          <GapCard
            key={gap.id}
            gap={gap}
            isSelected={selectedGaps.includes(gap.id)}
            onSelect={onSelect}
            onView={onView}
            viewMode="card"
            draggable
            onDragStart={(event, draggedGap) => {
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData('text/plain', draggedGap.id);
              setIsDragOver(false);
            }}
          />
        ))}
        
        {gaps.length === 0 && (
          <div className={`rounded-xl border border-dashed py-8 text-center text-muted-foreground transition-colors ${
            isDragOver ? 'border-primary/50 bg-background/70' : 'border-border/70'
          }`}>
            <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No gaps here</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ContentGapsPage() {
  useAuth();
  const location = useLocation();
  const initialTopicFilter = new URLSearchParams(location.search).get('topic') || 'all';
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedGaps, setSelectedGaps] = useState([]);
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const [bulkTopicValue, setBulkTopicValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [topicFilter, setTopicFilter] = useState(initialTopicFilter);
  const [viewMode, setViewMode] = useState('kanban'); // 'card', 'list', 'kanban'
  const [selectedGap, setSelectedGap] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateTopicDialog, setShowCreateTopicDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [newTopicName, setNewTopicName] = useState('');
  const [creatingTopic, setCreatingTopic] = useState(false);
  const [topics, setTopics] = useState([]);
  const [gapTopicOptions, setGapTopicOptions] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [generating, _setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  
  // Content Editor State
  const [showContentEditor, setShowContentEditor] = useState(false);
  const [, _setGeneratedContent] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [contentTitle, setContentTitle] = useState('');
  const [selectedGapsForContent, _setSelectedGapsForContent] = useState([]);
  
  // Metadata tags state - matching the document upload form
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedSubTopic, setSelectedSubTopic] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [shortSummary, setShortSummary] = useState('');
  const [selectedGroupVisibility, setSelectedGroupVisibility] = useState('all');
  
  // Groups and regions data
  const [groups, setGroups] = useState([]);
  const [regionsConfig, setRegionsConfig] = useState({});
  const [planLimits, setPlanLimits] = useState(null);
  
  // Quill editor configuration
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean']
    ],
  }), []);
  
  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'blockquote', 'code-block', 'link'
  ];
  
  // Get sub-topics for selected topic
  const getSubTopics = () => {
    if (!selectedTopic) return [];
    const topic = topics.find(t => t.id === selectedTopic);
    // GET /topics returns the nested array under `sub_topics`; nothing in the
    // API has ever returned `children`, so this used to be permanently empty.
    return topic?.sub_topics || [];
  };
  
  // Get countries for selected region
  const getCountries = () => {
    if (!selectedRegion) return [];
    return regionsConfig[selectedRegion] || [];
  };

  // Fetch data
  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [gapsRes, topicsRes, groupsRes, regionsRes, limitsRes] = await Promise.all([
        api.get('/content-gaps?limit=100'),
        api.get('/topics'),
        api.get('/groups').catch(() => ({ data: [] })),
        api.get('/groups/config/regions').catch(() => ({ data: { regions: {} } })),
        api.get('/settings/tenant/limits').catch(() => ({ data: null }))
      ]);
      
      const fetchedGaps = gapsRes.data.gaps || [];
      setGaps(fetchedGaps);
      setTopics(topicsRes.data || []);
      // Derive topic filter options from actual gap data
      const uniqueTopics = [...new Set(fetchedGaps.map(g => g.suggested_topic_name).filter(Boolean))].sort();
      setGapTopicOptions(uniqueTopics.map(name => ({ id: name, name })));
      setGroups(groupsRes.data || []);
      setRegionsConfig(regionsRes.data.regions || {});
      setPlanLimits(limitsRes.data);
    } catch (err) {
      console.error('Failed to fetch content gaps:', err);
      toast.error('Error', { description: 'Failed to load content gaps' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Re-fetch just the capacity/usage after a mutation so the limit meter
  // reflects freed slots immediately (resolving a gap frees capacity). Server
  // value stays authoritative, incl. tenants with more gaps than the page fetch.
  // A sequence guard drops out-of-order responses so rapid mutations can't let a
  // stale usage overwrite a newer one. (EIP-247)
  const limitsReqRef = useRef(0);
  const refreshLimits = async () => {
    const seq = ++limitsReqRef.current;
    try {
      const res = await api.get('/settings/tenant/limits');
      if (seq === limitsReqRef.current) setPlanLimits(res.data);
    } catch {
      /* keep the last known limits on failure */
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle selection
  const handleSelectGap = (gapId) => {
    setSelectedGaps(prev => 
      prev.includes(gapId) 
        ? prev.filter(id => id !== gapId)
        : [...prev, gapId]
    );
  };

  const handleSelectAll = () => {
    if (selectedGaps.length === filteredGaps.length) {
      setSelectedGaps([]);
    } else {
      setSelectedGaps(filteredGaps.map(g => g.id));
    }
  };

  // View gap details
  const handleViewGap = (gap) => {
    setSelectedGap(gap);
    setShowDetailModal(true);
  };

  const updateGapStatus = async (gapIds, newStatus, { clearSelection = true, showToast = true } = {}) => {
    const ids = Array.isArray(gapIds) ? gapIds : [gapIds];
    if (ids.length === 0) {
      toast.error('No gaps selected', { description: 'Please select gaps to update' });
      return;
    }

    // Map frontend status values to backend bulk-action action names
    const actionMap = { reviewed: 'mark_reviewed', addressed: 'mark_addressed', dismissed: 'dismiss' };
    const action = actionMap[newStatus] || newStatus;

    setUpdating(true);
    try {
      await api.post('/content-gaps/bulk-action', {
        gap_ids: ids,
        action
      });

      setGaps(prev => prev.map(gap => (
        ids.includes(gap.id) ? { ...gap, status: newStatus } : gap
      )));
      setSelectedGap(prev => (
        prev && ids.includes(prev.id) ? { ...prev, status: newStatus } : prev
      ));
      refreshLimits();
      if (showToast) {
        toast.success('Status Updated', { description: `${ids.length} gap${ids.length === 1 ? '' : 's'} marked as ${newStatus}` });
      }
      if (clearSelection) setSelectedGaps([]);
    } catch (err) {
      toast.error('Error', { description: err.response?.data?.detail || 'Failed to update status' });
    } finally {
      setUpdating(false);
      setBulkStatusValue('');
    }
  };

  // Update status
  const handleUpdateStatus = async (newStatus) => {
    await updateGapStatus(selectedGaps, newStatus);
  };

  const handleMoveGap = async (gapId, newStatus) => {
    const gap = gaps.find(item => item.id === gapId);
    if (!gap || gap.status === newStatus) return;
    await updateGapStatus([gapId], newStatus, { clearSelection: false });
  };

  const requestDeleteGaps = (gapIds) => {
    const ids = Array.isArray(gapIds) ? gapIds : [gapIds];
    if (ids.length === 0) {
      toast.error('No gaps selected', { description: 'Please select gaps to delete' });
      return;
    }
    setPendingDeleteIds(ids);
    setShowDeleteDialog(true);
  };

  const handleDeleteGaps = async () => {
    if (pendingDeleteIds.length === 0) return;

    setUpdating(true);
    try {
      const response = await api.post('/content-gaps/bulk-delete', {
        gap_ids: pendingDeleteIds
      });

      setGaps(prev => prev.filter(gap => !pendingDeleteIds.includes(gap.id)));
      setSelectedGaps(prev => prev.filter(id => !pendingDeleteIds.includes(id)));
      if (selectedGap && pendingDeleteIds.includes(selectedGap.id)) {
        setSelectedGap(null);
        setShowDetailModal(false);
      }
      setShowDeleteDialog(false);
      setPendingDeleteIds([]);
      refreshLimits();
      toast.success('Gaps Deleted', { description: response.data?.message || 'Selected gaps were deleted' });
    } catch (err) {
      toast.error('Error', { description: err.response?.data?.detail || 'Failed to delete gaps' });
    } finally {
      setUpdating(false);
    }
  };

  // Assign topic
  const handleAssignTopic = async (topicId) => {
    if (topicId === '__create_topic__') {
      setShowCreateTopicDialog(true);
      setBulkTopicValue('');
      return;
    }
    if (selectedGaps.length === 0) return;

    setUpdating(true);
    try {
      await api.post('/content-gaps/bulk-action', {
        gap_ids: selectedGaps,
        action: 'assign_topic',
        topic_id: topicId
      });

      toast.success('Topic Assigned', { description: `Topic assigned to ${selectedGaps.length} gaps` });
      const assignedTopic = topics.find(topic => topic.id === topicId);
      setGaps(prev => prev.map(gap => (
        selectedGaps.includes(gap.id)
          ? {
            ...gap,
            suggested_topic_id: topicId,
            suggested_topic_name: assignedTopic?.name || gap.suggested_topic_name,
            topic_color: assignedTopic?.color || gap.topic_color,
          }
          : gap
      )));
      setSelectedGaps([]);
    } catch (err) {
      toast.error('Error', { description: err.response?.data?.detail || 'Failed to assign topic' });
    } finally {
      setUpdating(false);
      setBulkTopicValue('');
    }
  };

  const handleCreateTopicAndAssign = async () => {
    const trimmedName = newTopicName.trim();
    if (!trimmedName) {
      toast.error('Topic name required', { description: 'Enter a topic name before creating it.' });
      return;
    }

    setCreatingTopic(true);
    try {
      const response = await api.post('/topics', {
        name: trimmedName,
        icon: 'Tag',
        color: '#6366f1',
      });
      const createdTopic = response.data;
      const topic = {
        id: createdTopic.id,
        name: createdTopic.name || trimmedName,
        color: createdTopic.color || '#6366f1',
        // Match the shape GET /topics returns, so getSubTopics() reads the same
        // key for an optimistically-added topic as for a fetched one.
        sub_topics: [],
      };
      setTopics(prev => [...prev, topic].sort((a, b) => a.name.localeCompare(b.name)));
      setShowCreateTopicDialog(false);
      setNewTopicName('');
      if (selectedGaps.length > 0) {
        await handleAssignTopic(topic.id);
      } else {
        toast.success('Topic Created', { description: `"${topic.name}" is ready to use.` });
      }
    } catch (err) {
      toast.error('Error', { description: err.response?.data?.detail || 'Failed to create topic' });
    } finally {
      setCreatingTopic(false);
    }
  };

  // Publish content to knowledge base
  const handlePublishContent = async () => {
    if (!editedContent.trim()) {
      toast.error('Error', { description: 'Please add content before publishing' });
      return;
    }
    
    if (!contentTitle.trim()) {
      toast.error('Error', { description: 'Please add a title before publishing' });
      return;
    }
    
    setPublishing(true);
    try {
      // Use the content-gaps publish endpoint which handles document creation
      const response = await api.post('/content-gaps/publish-to-kb', {
        title: contentTitle,
        content: editedContent,
        topic_id: selectedTopic || selectedGapsForContent[0]?.suggested_topic_id || null,
        sub_topic_id: selectedSubTopic,
        topic_name: selectedGapsForContent[0]?.suggested_topic_name || 'General',
        gap_ids: selectedGapsForContent.map(g => g.id),
        region: selectedRegion,
        country: selectedCountry,
        short_summary: shortSummary,
        group_visibility: selectedGroupVisibility
      });
      
      toast.success('Published!', { description: response.data.message || 'Content has been added to the knowledge base' });
      setShowContentEditor(false);
      setSelectedGaps([]);
      // Reset editor state
      setSelectedTopic('');
      setSelectedSubTopic('');
      setSelectedRegion('');
      setSelectedCountry('');
      setShortSummary('');
      setSelectedGroupVisibility('all');
      fetchData();
    } catch (err) {
      console.error('Publish error:', err);
      toast.error('Error', { description: err.response?.data?.detail || 'Failed to publish content. Try saving as draft.' });
    } finally {
      setPublishing(false);
    }
  };

  // Filter gaps
  const filteredGaps = gaps.filter(gap => {
    const matchesSearch = !searchQuery || 
      gap.query?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      gap.response?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || gap.status === statusFilter;
    const matchesType = typeFilter === 'all' || gap.gap_type === typeFilter;
    
    // Topic filter - match by suggested_topic_name (derived from gap data)
    let matchesTopic = topicFilter === 'all';
    if (!matchesTopic) {
      if (topicFilter === 'unassigned') {
        matchesTopic = !gap.suggested_topic_id && !gap.suggested_topic_name;
      } else {
        matchesTopic = gap.suggested_topic_name === topicFilter;
      }
    }
    
    return matchesSearch && matchesStatus && matchesType && matchesTopic;
  });

  // Group gaps for kanban
  const kanbanGroups = {
    new: filteredGaps.filter(g => g.status === 'new'),
    reviewed: filteredGaps.filter(g => g.status === 'reviewed'),
    addressed: filteredGaps.filter(g => g.status === 'addressed'),
    dismissed: filteredGaps.filter(g => g.status === 'dismissed'),
  };

  const calculatedTotals = {
    total_gaps: gaps.length,
    new_gaps: gaps.filter(g => g.status === 'new').length,
    reviewed_gaps: gaps.filter(g => g.status === 'reviewed').length,
    addressed_gaps: gaps.filter(g => g.status === 'addressed').length,
    dismissed_gaps: gaps.filter(g => g.status === 'dismissed').length,
  };

  // Keep KPI counts, filters, and kanban columns anchored to the same loaded data.
  // The stats endpoint can include a different aggregation window, which made cards disagree.
  const totals = calculatedTotals;
  const contentGapLimits = planLimits?.plan_limits || planLimits?.limits;
  const contentGapUsage = planLimits?.usage || {};
  const contentGapLimit = contentGapLimits?.content_gap_limit;
  // Only open gaps (new + reviewed) consume capacity; addressed/dismissed are
  // terminal and free their slot. Falls back to a local open count if the
  // usage payload is unavailable. (EIP-247)
  const openGapCount = (totals.new_gaps || 0) + (totals.reviewed_gaps || 0);
  const contentGapUsed = contentGapUsage.content_gaps ?? openGapCount;
  const selectedTypeOption = TYPE_FILTER_OPTIONS.find(option => option.value === typeFilter) || TYPE_FILTER_OPTIONS[0];

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="7xl"
        banner
        bannerPageKey="kb-gaps"
        kpis={4}
        sections={2}
        sectionClassName="h-80"
        data-testid="content-gaps-loading"
      />
    );
  }

  const gapList = (
    <>
      <DataTableToolbar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search gaps..."
      >
          {/* Type filter */}
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger
              className="w-40"
              aria-label="Filter by gap type"
              title={selectedTypeOption.label}
            >
              <span className="truncate">{selectedTypeOption.label}</span>
            </SelectTrigger>
            <SelectContent>
              {TYPE_FILTER_OPTIONS.map(option => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  textValue={option.label}
                  title={option.label}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Topic filter */}
          <Select value={topicFilter} onValueChange={setTopicFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by Topic" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Topics</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {gapTopicOptions.map(topic => (
                <SelectItem key={topic.id} value={topic.id}>
                  <span className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full bg-indigo-500"
                    />
                    {topic.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <ViewModeToggle
            value={viewMode}
            onChange={setViewMode}
            options={[
              { value: 'card', label: 'Card view', icon: LayoutGrid },
              { value: 'list', label: 'Table view', icon: List },
              { value: 'kanban', label: 'Kanban view', icon: Columns },
            ]}
          />
        
        {/* Bulk actions */}
        <div className="flex flex-wrap items-center gap-2">
          {selectedGaps.length > 0 && (
            <>
              <span className="text-sm text-muted-foreground">
                {selectedGaps.length} selected
              </span>
              
              <Select value={bulkStatusValue} onValueChange={(value) => {
                setBulkStatusValue(value);
                handleUpdateStatus(value);
              }}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Set Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reviewed">Mark Reviewed</SelectItem>
                  <SelectItem value="addressed">Mark Addressed</SelectItem>
                  <SelectItem value="dismissed">Dismiss</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={bulkTopicValue} onValueChange={(value) => {
                setBulkTopicValue(value);
                handleAssignTopic(value);
              }}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Assign Topic" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__create_topic__">Create new topic...</SelectItem>
                  {topics.map(topic => (
                    <SelectItem key={topic.id} value={topic.id}>
                      {topic.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => requestDeleteGaps(selectedGaps)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
              
              {/* Generate Content button disabled — creates 0-page KB docs without real content (Bug #38) */}
            </>
          )}
        </div>
      </DataTableToolbar>

      {viewMode === 'kanban' ? (
        <div className="flex gap-6 overflow-x-auto pb-4">
          <KanbanColumn
            title="New"
            status="new"
            gaps={kanbanGroups.new}
            selectedGaps={selectedGaps}
            onSelect={handleSelectGap}
            onView={handleViewGap}
            onMoveGap={handleMoveGap}
          />
          <KanbanColumn
            title="Reviewed"
            status="reviewed"
            gaps={kanbanGroups.reviewed}
            selectedGaps={selectedGaps}
            onSelect={handleSelectGap}
            onView={handleViewGap}
            onMoveGap={handleMoveGap}
          />
          <KanbanColumn
            title="Addressed"
            status="addressed"
            gaps={kanbanGroups.addressed}
            selectedGaps={selectedGaps}
            onSelect={handleSelectGap}
            onView={handleViewGap}
            onMoveGap={handleMoveGap}
          />
          <KanbanColumn
            title="Dismissed"
            status="dismissed"
            gaps={kanbanGroups.dismissed}
            selectedGaps={selectedGaps}
            onSelect={handleSelectGap}
            onView={handleViewGap}
            onMoveGap={handleMoveGap}
          />
        </div>
      ) : viewMode === 'list' ? (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="flex items-center gap-4 p-4 border-b border-border bg-muted/30">
            <Checkbox 
              checked={selectedGaps.length === filteredGaps.length && filteredGaps.length > 0}
              onCheckedChange={handleSelectAll}
            />
            <span className="text-sm font-medium text-muted-foreground">
              {filteredGaps.length} gaps
            </span>
          </div>
          
          {filteredGaps.map(gap => (
            <GapCard
              key={gap.id}
              gap={gap}
              isSelected={selectedGaps.includes(gap.id)}
              onSelect={handleSelectGap}
              onView={handleViewGap}
              viewMode="list"
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredGaps.map(gap => (
            <GapCard
              key={gap.id}
              gap={gap}
              isSelected={selectedGaps.includes(gap.id)}
              onSelect={handleSelectGap}
              onView={handleViewGap}
              viewMode="card"
            />
          ))}
        </div>
      )}

      {filteredGaps.length === 0 && (
        <EmptyState
          icon={CheckCircle}
          title="No content gaps found"
          description={
            searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
              ? 'Try adjusting your filters.'
              : 'Your knowledge base is covering employee questions well.'
          }
          variant="panel"
        />
      )}
    </>
  );

  return (
    <PageShell maxWidth="7xl" data-testid="content-gaps-page">
        <PageBanner pageKey="kb-gaps" />

        <PageHeader
          title="Content Gaps"
          description="Questions Eva couldn't answer well — opportunities to improve your knowledge base"
          actions={(
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <button type="button" onClick={() => setStatusFilter('all')} className="block h-full w-full text-left">
            <KpiCard
              title="Total Gaps"
              value={totals.total_gaps || 0}
              icon={FileQuestion}
              color="primary"
              className={statusFilter === 'all' ? 'ring-2 ring-primary ring-offset-2' : ''}
            />
          </button>
          <button type="button" onClick={() => setStatusFilter('new')} className="block h-full w-full text-left">
            <KpiCard
              title="New"
              value={totals.new_gaps || 0}
              subtitle="Needs review"
              icon={STATUS_CONFIG.new.icon}
              color={STATUS_CONFIG.new.kpiColor}
              className={statusFilter === 'new' ? 'ring-2 ring-primary ring-offset-2' : ''}
            />
          </button>
          <button type="button" onClick={() => setStatusFilter('reviewed')} className="block h-full w-full text-left">
            <KpiCard
              title="Reviewed"
              value={totals.reviewed_gaps || 0}
              icon={STATUS_CONFIG.reviewed.icon}
              color={STATUS_CONFIG.reviewed.kpiColor}
              className={statusFilter === 'reviewed' ? 'ring-2 ring-primary ring-offset-2' : ''}
            />
          </button>
          <button type="button" onClick={() => setStatusFilter('addressed')} className="block h-full w-full text-left">
            <KpiCard
              title="Addressed"
              value={totals.addressed_gaps || 0}
              icon={STATUS_CONFIG.addressed.icon}
              color={STATUS_CONFIG.addressed.kpiColor}
              className={statusFilter === 'addressed' ? 'ring-2 ring-primary ring-offset-2' : ''}
            />
          </button>
          <button type="button" onClick={() => setStatusFilter('dismissed')} className="block h-full w-full text-left">
            <KpiCard
              title="Dismissed"
              value={totals.dismissed_gaps || 0}
              icon={STATUS_CONFIG.dismissed.icon}
              color={STATUS_CONFIG.dismissed.kpiColor}
              className={statusFilter === 'dismissed' ? 'ring-2 ring-primary ring-offset-2' : ''}
            />
          </button>
        </div>

        {contentGapLimits && (
          <LimitMeter
            title="Content Gaps"
            used={contentGapUsed}
            limit={contentGapLimit}
            icon={FileQuestion}
            displayType="capacity"
            caption="Content gap workspace capacity"
            explanation="Only new and reviewed gaps count toward capacity. Addressing or dismissing a gap frees a slot; resolved gaps are kept for reporting until deleted."
            showManagePlanCta
            testId="content-gap-limit-meter"
          />
        )}

        {gapList}

      <Dialog open={showCreateTopicDialog} onOpenChange={setShowCreateTopicDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Topic</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="new-topic-name">Topic name</Label>
              <Input
                id="new-topic-name"
                value={newTopicName}
                onChange={(event) => setNewTopicName(event.target.value)}
                placeholder="e.g. Benefits Eligibility"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleCreateTopicAndAssign();
                  }
                }}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              The topic will be created and assigned to the selected gap{selectedGaps.length === 1 ? '' : 's'}.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTopicDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTopicAndAssign} disabled={creatingTopic}>
              {creatingTopic && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create and Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) setPendingDeleteIds([]);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Content Gaps</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>
              Delete {pendingDeleteIds.length} selected content gap{pendingDeleteIds.length === 1 ? '' : 's'}?
            </p>
            <p>This removes the gap record from this triage workflow.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteGaps}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedGap && (
                <>
                  <div className={`p-2 rounded-lg ${GAP_TYPE_CONFIG[selectedGap.gap_type]?.iconBg || 'bg-muted text-muted-foreground'}`}>
                    {(() => {
                      const Icon = GAP_TYPE_CONFIG[selectedGap.gap_type]?.icon || HelpCircle;
                      return <Icon className="w-5 h-5" />;
                    })()}
                  </div>
                  Gap Details
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedGap && (
            <div className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Question Asked</Label>
                <p className="text-lg font-medium mt-1">"{selectedGap.query}"</p>
              </div>
              
              <div>
                <Label className="text-muted-foreground">Eva's Response</Label>
                <div className="mt-1 p-3 bg-muted rounded-lg">
                  {selectedGap.response
                    ? <MarkdownContent text={selectedGap.response} />
                    : <p className="text-sm">No response recorded</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="block text-muted-foreground">Gap Type</Label>
                  <Badge className={`mt-1 ${resolveGapTypeConfig(selectedGap.gap_type).color}`}>
                    {resolveGapTypeConfig(selectedGap.gap_type).label}
                  </Badge>
                </div>
                <div>
                  <Label className="block text-muted-foreground">Status</Label>
                  <Badge className={`mt-1 ${STATUS_CONFIG[selectedGap.status]?.color}`}>
                    {STATUS_CONFIG[selectedGap.status]?.label || selectedGap.status}
                  </Badge>
                </div>
              </div>
              
              {selectedGap.user_department && (
                <div>
                  <Label className="text-muted-foreground">User Department</Label>
                  <p className="mt-1">{selectedGap.user_department}</p>
                </div>
              )}
              
              {/* Location info */}
              {(selectedGap.user_country || selectedGap.user_region) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedGap.user_country && (
                    <div>
                      <Label className="text-muted-foreground">Country</Label>
                      <p className="mt-1 flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {selectedGap.user_country}
                      </p>
                    </div>
                  )}
                  {selectedGap.user_region && (
                    <div>
                      <Label className="text-muted-foreground">Region</Label>
                      <p className="mt-1">{selectedGap.user_region}</p>
                    </div>
                  )}
                </div>
              )}
              
              <div>
                <Label className="text-muted-foreground">Created</Label>
                <p className="mt-1">{new Date(selectedGap.created_at).toLocaleString()}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {selectedGap && (
              <Button variant="destructive" onClick={() => {
                setShowDetailModal(false);
                requestDeleteGaps([selectedGap.id]);
              }}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
            {selectedGap && (selectedGap.status === 'new' || selectedGap.status === 'reviewed') && (
              <Button
                variant="outline"
                disabled={updating}
                onClick={async () => {
                  await updateGapStatus([selectedGap.id], 'dismissed', { clearSelection: false });
                  setShowDetailModal(false);
                }}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Dismiss
              </Button>
            )}
            {selectedGap && selectedGap.status === 'new' && (
              <Button disabled={updating} onClick={async () => {
                await updateGapStatus([selectedGap.id], 'reviewed', { clearSelection: false });
                setShowDetailModal(false);
              }}>
                <Eye className="mr-2 h-4 w-4" />
                Mark Reviewed
              </Button>
            )}
            {selectedGap && selectedGap.status === 'reviewed' && (
              <Button disabled={updating} onClick={async () => {
                await updateGapStatus([selectedGap.id], 'addressed', { clearSelection: false });
                setShowDetailModal(false);
              }}>
                <CheckCircle className="mr-2 h-4 w-4" />
                Mark Addressed
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content Editor Canvas with Stylized Background */}
      <Dialog open={showContentEditor} onOpenChange={setShowContentEditor}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden flex flex-col p-0 border-0">
          {/* Stylized gradient background header */}
          <div className="bg-gradient-to-br from-orange-50 via-amber-50/30 to-yellow-50/20 p-6 border-b">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 text-xl">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-orange-500/20">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <span>Eva Spark</span>
                <Badge variant="outline" className="text-xs font-normal">Content Generator</Badge>
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Creating content to address {selectedGapsForContent.length} question{selectedGapsForContent.length !== 1 ? 's' : ''}
              </p>
            </DialogHeader>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gradient-to-b from-white to-slate-50/50">
            {generating ? (
              <div className="flex-1 flex flex-col items-center justify-center py-16">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 animate-pulse flex items-center justify-center shadow-xl shadow-orange-500/30">
                    <Zap className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute inset-0 w-20 h-20 rounded-full border-4 border-orange-300 border-t-transparent animate-spin" />
                </div>
                <p className="mt-6 text-lg font-semibold text-foreground">Eva Spark is generating...</p>
                <p className="text-sm text-muted-foreground">Igniting new content from your knowledge gaps</p>
              </div>
            ) : (
              <>
                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="content-title" className="text-sm font-semibold text-slate-700">Title</Label>
                  <Input
                    id="content-title"
                    value={contentTitle}
                    onChange={(e) => setContentTitle(e.target.value)}
                    placeholder="Enter article title..."
                    className="h-12 text-base border-slate-200 focus:border-orange-400 focus:ring-orange-400/20 rounded-xl"
                  />
                  <p className="text-xs text-muted-foreground">
                    Addressing: {selectedGapsForContent.map(g => `"${g.query?.slice(0, 30)}..."`).join(', ')}
                  </p>
                </div>

                {/* Topic & Sub-Topic Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700">Topic</Label>
                    <Select value={selectedTopic} onValueChange={(val) => { setSelectedTopic(val); setSelectedSubTopic(''); }}>
                      <SelectTrigger className="h-12 border-slate-200 rounded-xl">
                        <SelectValue placeholder="Select topic" />
                      </SelectTrigger>
                      <SelectContent>
                        {topics.map(topic => (
                          <SelectItem key={topic.id} value={topic.id}>
                            {topic.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700">Sub-Topic (select topic first)</Label>
                    <Select 
                      value={selectedSubTopic} 
                      onValueChange={setSelectedSubTopic}
                      disabled={!selectedTopic || getSubTopics().length === 0}
                    >
                      <SelectTrigger className="h-12 border-slate-200 rounded-xl">
                        <SelectValue placeholder={selectedTopic ? "Select sub-topic" : "Select topic first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {getSubTopics().map(subTopic => (
                          <SelectItem key={subTopic.id} value={subTopic.id}>
                            {subTopic.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Region & Country Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700">Region</Label>
                    <Select value={selectedRegion} onValueChange={(val) => { setSelectedRegion(val); setSelectedCountry(''); }}>
                      <SelectTrigger className="h-12 border-slate-200 rounded-xl">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(regionsConfig).map(region => (
                          <SelectItem key={region} value={region}>
                            {region === 'NA' ? 'North America' : 
                             region === 'EMEA' ? 'Europe, Middle East & Africa' :
                             region === 'APAC' ? 'Asia Pacific' :
                             region === 'LATAM' ? 'Latin America' : region}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700">Country (select region first)</Label>
                    <Select 
                      value={selectedCountry} 
                      onValueChange={setSelectedCountry}
                      disabled={!selectedRegion || getCountries().length === 0}
                    >
                      <SelectTrigger className="h-12 border-slate-200 rounded-xl">
                        <SelectValue placeholder={selectedRegion ? "Select country" : "Select region first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {getCountries().map(country => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Short Summary */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Short Summary</Label>
                  <textarea
                    value={shortSummary}
                    onChange={(e) => setShortSummary(e.target.value)}
                    placeholder="Brief description of the article content..."
                    className="w-full h-24 p-3 text-sm border border-slate-200 rounded-xl resize-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 focus:outline-none"
                  />
                </div>

                {/* Long Summary (Quill Editor) */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Long Summary</Label>
                  <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                    <ReactQuill
                      theme="snow"
                      value={editedContent}
                      onChange={setEditedContent}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Write the full article content here..."
                      className="[&_.ql-container]:min-h-[200px] [&_.ql-editor]:min-h-[200px] [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-slate-200 [&_.ql-container]:border-0"
                    />
                  </div>
                </div>

                {/* Group Visibility */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Group Visibility</Label>
                  <Select value={selectedGroupVisibility} onValueChange={setSelectedGroupVisibility}>
                    <SelectTrigger className="h-12 border-orange-200 border-2 rounded-xl bg-white">
                      <SelectValue placeholder="Select visibility" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Groups (Everyone)</SelectItem>
                      {groups.map(group => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Who can see this document in Eva</p>
                </div>
              </>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t bg-slate-50/80">
            <Button 
              variant="outline" 
              onClick={() => setShowContentEditor(false)}
              disabled={generating || publishing}
              className="h-11 px-6 rounded-xl border-slate-300"
            >
              Cancel
            </Button>
            
            <Button
              onClick={handlePublishContent}
              disabled={generating || publishing || !editedContent.trim() || !contentTitle.trim()}
              className="h-11 px-6 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg shadow-orange-500/30"
            >
              {publishing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save & Publish
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
